import AppearInPlace from "@/comps/animate/appear-in-place"
import SwitchOnChange from "@/comps/animate/switch-on-change"

import { CustomThemedResource } from "@/comps/image"
import Toggle from "@/comps/toggle"
import Tooltip from "@/comps/tooltip"

import {
	DesktopOrTablet,
	Mobile,
	onTablet,
} from "@/utils/breakpoints"

import { useTranslation } from "next-i18next"
import { useTheme } from "next-themes"
import { useEffect, useState } from "react"
import { FlexBox } from "react-styled-flex"
import styled, { css } from "styled-components"

import { assetUrl } from "@/utils/cdn"

const ThemeContainer = styled.div<{ forced: boolean }>`
	display: flex;
	flex-shrink: 0;

	padding: 8px;
	border-radius: 10px;

	transition: background-color 200ms;
	transition-timing-function: ease-in-out;
	cursor: pointer;
	user-select: none;

	margin: 25px 0;
	pointer-events: unset;

	:hover {
		background-color: var(--color-blue-100);
	}

	${(props) =>
		props.forced &&
		css`
			display: none;
		`}
`

const ThemeToggle = () => {
	const { resolvedTheme, forcedTheme, setTheme } =
		useTheme()

	const [solid, setSolid] = useState(true)
	const [isDark, setIsDark] = useState(false)

	const { t } = useTranslation()

	const toggleAction = () => {
		const name = resolvedTheme === "dark" ? "light" : "dark"
		setTheme(name)
		if (solid) setSolid(false)
	}

	useEffect(() => {
		if (!resolvedTheme) {
			return
		}

		setIsDark(resolvedTheme === "dark")
	}, [resolvedTheme])

	return (
		<>
			<DesktopOrTablet>
				<Tooltip
					title={t("common:txt_change_theme")}
					disabled={true}
					margin={-20}
					width={100}>
					<ThemeContainer
						onClick={toggleAction}
						forced={forcedTheme !== undefined}>
						<FlexBox column center>
							<FlexBox center>
								<AppearInPlace>
									<SwitchOnChange
										index={isDark ? 1 : 0}
										short
										solid={solid}>
										<img
											src={assetUrl(
												"/comps/toggle-light.webp",
											)}
											alt="Theme toggle icon light"
											className="h-6 w-6"
										/>
										<img
											src={assetUrl(
												"/comps/toggle-dark.svg",
											)}
											className="h-6 w-6"
											alt="toggle icon"
										/>
									</SwitchOnChange>
								</AppearInPlace>
							</FlexBox>
						</FlexBox>
					</ThemeContainer>
				</Tooltip>
			</DesktopOrTablet>
			<Mobile>
				{forcedTheme === undefined && (
					<Toggle onChange={toggleAction} theme={isDark} />
				)}
			</Mobile>
		</>
	)
}

export const ThemeSwitch = () => {
	const [mounted, setMounted] = useState(false)
	const { theme, setTheme } = useTheme()

	const { t } = useTranslation()

	useEffect(() => {
		setMounted(true)
	}, [])

	if (!mounted) {
		return null
	}

	return (
		<div className="relative flex h-[30px] w-[136px] flex-row items-center rounded-[6px] bg-blue-100 p-[2px]">
			<div
				style={{
					left:
						theme === "system"
							? "2px"
							: theme === "dark"
							? "46px"
							: "90px",
				}}
				className="absolute top-[2px] z-[1] h-[26px] w-11 rounded-[5px] bg-color-popup-cell transition-all duration-200"
			/>

			<Tooltip
				title={t("txt_system")}
				showAlways
				className="cursor-pointer"
				margin={5}
				width={60}>
				<button
					onClick={(e) => {
						e.preventDefault()
						setTheme("system")
					}}
					className="z-[2] flex h-full w-11 items-center justify-center [--icon-color:var(--color-blue-700)]">
					<SystemIcon />
				</button>
			</Tooltip>

			<Tooltip
				title={t("txt_dark")}
				showAlways
				className="cursor-pointer"
				margin={5}
				width={60}>
				<button
					onClick={(e) => {
						e.preventDefault()
						setTheme("dark")
					}}
					className="z-[2] flex h-full w-11 items-center justify-center [--icon-color:var(--color-blue-700)]">
					<DarkIcon />
				</button>
			</Tooltip>

			<Tooltip
				title={t("txt_light")}
				showAlways
				className="cursor-pointer"
				margin={5}
				width={60}>
				<button
					onClick={(e) => {
						e.preventDefault()
						setTheme("light")
					}}
					className="z-[2] flex h-full w-11 items-center justify-center [--icon-color:var(--color-blue-700)]">
					<LightIcon />
				</button>
			</Tooltip>
		</div>
	)
}

const BigLogo = styled.div<{
	showOnMobile: boolean
}>`
	${(props) =>
		!props.showOnMobile &&
		css`
			display: none;
		`};

	${onTablet(css`
		display: flex;
		flex-shrink: 0;

		position: relative;
	`)}
`

const LogoLink = styled.a`
	margin-bottom: 12px;
	margin-right: 20px;
`

export function adExamplesIconMobile(
	width?: string,
	height?: string,
) {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 20 20"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M14.9887 2.40454L13.55 1.9887C13.4835 1.96892 13.4126 1.96846 13.3458 1.9874C13.279 2.00633 13.2189 2.04386 13.1726 2.09562C13.1409 2.13176 13.1168 2.17383 13.1016 2.21938C13.0864 2.26494 13.0804 2.31306 13.084 2.36095C13.0876 2.40884 13.1007 2.45554 13.1225 2.49832C13.1443 2.54111 13.1744 2.57912 13.2111 2.61014L14.3392 3.5953C14.5764 3.8018 14.7434 4.05561 14.8316 4.32985L14.8374 4.35207C14.924 4.63498 14.9284 4.94277 14.8437 5.24738L14.4477 6.66307C14.4294 6.73004 14.4304 6.80084 14.4507 6.86725C14.4709 6.93366 14.5096 6.99297 14.5622 7.03831C14.6354 7.10238 14.7308 7.13518 14.8279 7.12964C14.9249 7.1241 15.016 7.08066 15.0814 7.00867L16.0585 5.9089C16.2592 5.67978 16.5217 5.51331 16.8146 5.42939C17.1114 5.34779 17.4251 5.35122 17.7201 5.43928L19.1592 5.85431C19.2258 5.87409 19.2966 5.87454 19.3634 5.85561C19.4303 5.83668 19.4903 5.79915 19.5367 5.74738C19.5683 5.71125 19.5924 5.66918 19.6076 5.62362C19.6228 5.57807 19.6288 5.52994 19.6252 5.48205C19.6216 5.43416 19.6085 5.38747 19.5867 5.34468C19.5649 5.3019 19.5348 5.26389 19.4981 5.23287L18.3696 4.24852C18.1264 4.03558 17.9559 3.77459 17.8701 3.49213C17.7843 3.20966 17.7799 2.90187 17.8651 2.59644L18.2603 1.18031C18.2786 1.11334 18.2776 1.04254 18.2573 0.976129C18.237 0.909716 18.1984 0.850406 18.1458 0.80507C18.0726 0.740993 17.9772 0.708196 17.8801 0.713738C17.783 0.71928 17.692 0.762717 17.6266 0.834702L16.6508 1.9341C16.4457 2.16468 16.1922 2.32543 15.9164 2.40859L15.8947 2.41362C15.5978 2.49562 15.2839 2.49247 14.9887 2.40454Z"
				fill="var(--icon-color)"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M3.33464 4.40527C2.93534 4.40527 2.41797 4.82792 2.41797 5.6078V15.3704C2.41797 16.1503 2.93534 16.573 3.33464 16.573H16.668C17.0673 16.573 17.5846 16.1503 17.5846 15.3704L17.5846 9.23563C17.5846 8.82142 17.9204 8.48563 18.3346 8.48563C18.7488 8.48563 19.0846 8.82142 19.0846 9.23563L19.0846 15.3704C19.0846 16.7473 18.1096 18.073 16.668 18.073H3.33464C1.89298 18.073 0.917969 16.7473 0.917969 15.3704V5.6078C0.917969 4.23098 1.89298 2.90527 3.33464 2.90527H10.8267C11.2409 2.90527 11.5767 3.24106 11.5767 3.65527C11.5767 4.06949 11.2409 4.40527 10.8267 4.40527H3.33464Z"
				fill="var(--icon-color)"
				stroke="var(--icon-color)"
				strokeWidth="0.2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M11.1934 13.6812C10.7402 13.6812 10.4746 13.4077 10.4746 12.9312V8.79443C10.4746 8.31787 10.7402 8.04443 11.1934 8.04443H12.7637C14.4668 8.04443 15.4668 9.03271 15.4668 10.8296C15.4668 12.6304 14.4629 13.6812 12.7637 13.6812H11.1934ZM11.9082 12.5249H12.5762C13.5098 12.5249 14.0098 11.9585 14.0098 10.8335C14.0098 9.7749 13.4824 9.20068 12.5762 9.20068H11.9082V12.5249Z"
				fill="var(--icon-color)"
			/>
			<path
				d="M5.26367 13.7671C4.81055 13.7671 4.5332 13.5093 4.5332 13.0835C4.5332 12.9624 4.57227 12.7866 4.63477 12.6147L6.02539 8.79443C6.23242 8.22021 6.58008 7.9585 7.13867 7.9585C7.72852 7.9585 8.07227 8.2085 8.28711 8.79443L9.68945 12.6147C9.75586 12.7983 9.7832 12.9312 9.7832 13.0796C9.7832 13.4819 9.48242 13.7671 9.06445 13.7671C8.65039 13.7671 8.43555 13.5796 8.31055 13.1187L8.11914 12.4937H6.18945L5.99805 13.0952C5.86133 13.5718 5.64648 13.7671 5.26367 13.7671ZM6.47461 11.4116H7.80664L7.14648 9.23975H7.11523L6.47461 11.4116Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const textToReelIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M9.69785 8.90874C10.1605 9.37271 10.0834 10.146 9.46653 10.4553L5.14851 13.0072C3.9919 13.7031 2.60396 13.2392 1.60156 11.6152C1.90999 13.7805 4.30033 15.1724 6.45934 13.9351L10.1605 11.7699C11.8569 10.842 11.7798 8.36744 10.0834 7.43949L5.84248 5.04229C5.99669 4.42366 6.69066 4.11435 7.30752 4.42366L11.6255 6.97551C12.7822 7.67147 13.1677 9.0634 12.3195 10.61C13.8617 9.68203 14.3243 6.82086 11.934 5.42894L8.07859 3.1864C6.38223 2.18112 4.37743 3.34106 4.45454 5.27428V10.378C3.83768 10.61 3.14371 10.146 3.14371 9.45004V4.42366C3.14371 3.10907 3.91479 1.87181 5.99669 1.87181C3.9919 0.94386 1.60156 2.49044 1.67867 4.96496V9.45004C1.67867 11.3833 3.76057 12.5432 5.37983 11.5379L9.69785 8.90874ZM8.23281 8.05812L5.84248 6.74353V9.45004L8.23281 8.05812Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const deforumIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M14.5892 9.00338C14.7914 7.67735 14.5888 6.32119 14.008 5.1121C13.4272 3.90302 12.4952 2.89725 11.3338 2.22622C10.1724 1.55519 8.83553 1.25012 7.49798 1.35086C6.16042 1.45161 4.88436 1.95349 3.83655 2.79092C2.78874 3.62835 2.0179 4.76238 1.62478 6.04482C1.23166 7.32726 1.23453 8.69847 1.63302 9.97925C2.03151 11.26 2.80709 12.3908 3.8584 13.2239C4.90971 14.0569 6.18786 14.5534 7.52582 14.6486"
				stroke="var(--icon-color)"
				strokeWidth="1.37143"
				strokeLinecap="round"
			/>
			<path
				d="M6.09912 7.99544V7.15097C6.09912 6.09917 6.84246 5.66935 7.75267 6.19525L8.48589 6.62001L9.21911 7.04477C10.1293 7.57067 10.1293 8.43031 9.21911 8.95621L8.48589 9.38098L7.75267 9.80574C6.84246 10.3316 6.09912 9.90182 6.09912 8.85002V7.99544Z"
				stroke="var(--icon-color)"
				strokeWidth="1.37143"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M15.1492 14.7447V11.1104C15.1249 10.427 14.2419 10.4265 14.2173 11.1104V14.7447C14.2417 15.4281 15.1246 15.4286 15.1492 14.7447ZM12.515 11.4427C12.3541 10.9273 11.9175 10.5955 11.4036 10.5977C10.8889 10.5997 10.455 10.9352 10.2979 11.4525C10.2945 11.4639 9.57062 14.6307 9.57062 14.6307C9.50766 14.9064 9.67946 15.181 9.95435 15.2441C10.2295 15.3073 10.5032 15.135 10.5661 14.8593L10.7034 14.2586H12.1345L12.278 14.8635C12.3422 15.1407 12.6259 15.3091 12.893 15.2432C13.1674 15.1777 13.3369 14.9016 13.2716 14.6266C13.2716 14.6266 12.5185 11.4541 12.515 11.4427ZM10.9551 13.3932L11.2891 11.663C11.3146 11.5768 11.3587 11.5296 11.4144 11.5293H11.4149C11.4708 11.5293 11.5138 11.5747 11.5397 11.6607L11.887 13.3932H10.9551Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const restlyeIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<g clipPath="url(#clip0_18567_138177)">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M5.90706 14.4009V10.7667C5.88266 10.0832 4.9997 10.0827 4.9751 10.7667V14.4009C4.99949 15.0844 5.88245 15.0849 5.90706 14.4009ZM3.2728 11.099C3.11195 10.5835 2.67529 10.2518 2.16139 10.2539C1.64676 10.256 1.21278 10.5915 1.05575 11.1087C1.05228 11.1202 0.328431 14.287 0.328431 14.287C0.265468 14.5626 0.437277 14.8373 0.712161 14.9004C0.987301 14.9635 1.26099 14.7912 1.32395 14.5156L1.46119 13.9149H2.89234L3.03586 14.5198C3.10002 14.797 3.38369 14.9654 3.65084 14.8994C3.92519 14.834 4.09472 14.5579 4.02944 14.2828C4.02944 14.2828 3.27635 11.1104 3.2728 11.099ZM1.71338 13.0495L2.04742 11.3192C2.07286 11.2331 2.11699 11.1858 2.17273 11.1855H2.17322C2.22911 11.1855 2.27207 11.2309 2.298 11.317L2.64534 13.0495H1.71338Z"
					fill="var(--icon-color)"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M1.98849 5.40636C1.98849 4.501 2.17422 4.13241 2.40788 3.93812C2.68309 3.70927 3.2302 3.53927 4.38146 3.53927H9.06756C10.2188 3.53927 10.7659 3.70927 11.0411 3.93812C11.2748 4.13241 11.4605 4.501 11.4605 5.40636V10.5978C11.4605 11.5014 11.2737 11.8707 11.0389 12.0656C10.7624 12.2951 10.2145 12.4649 9.06756 12.4649H6.87511V13.9221H9.06756C10.2563 13.9221 11.2692 13.7682 11.9696 13.1869C12.7116 12.571 12.9178 11.6424 12.9178 10.5978V5.40636C12.9178 4.3634 12.7142 3.43413 11.9729 2.81765C11.273 2.23572 10.2593 2.08203 9.06756 2.08203H4.38146C3.18968 2.08203 2.17599 2.23572 1.47617 2.81765C0.734788 3.43413 0.53125 4.3634 0.53125 5.40636V10.0829C0.879247 9.641 1.39499 9.35245 1.98849 9.31634V5.40636Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M2.40788 3.93812L2.33481 3.85025L2.40788 3.93812ZM11.0411 3.93812L10.9681 4.02599V4.02599L11.0411 3.93812ZM11.0389 12.0656L10.9659 11.9776L11.0389 12.0656ZM6.87511 12.4649V12.3506C6.81199 12.3506 6.76082 12.4017 6.76082 12.4649H6.87511ZM6.87511 13.9221H6.76082C6.76082 13.9852 6.81199 14.0364 6.87511 14.0364V13.9221ZM11.9696 13.1869L12.0425 13.2749L11.9696 13.1869ZM11.9729 2.81765L11.8998 2.90552L11.9729 2.81765ZM1.47617 2.81765L1.54924 2.90552L1.47617 2.81765ZM0.53125 10.0829H0.416964C0.416964 10.1315 0.44782 10.1749 0.493831 10.1908C0.539843 10.2068 0.590903 10.1918 0.621033 10.1536L0.53125 10.0829ZM1.98849 9.31634L1.99543 9.43041C2.05574 9.42674 2.10277 9.37676 2.10277 9.31634H1.98849ZM2.33481 3.85025C2.0615 4.07751 1.8742 4.48983 1.8742 5.40636H2.10277C2.10277 4.51216 2.28694 4.18732 2.48095 4.02599L2.33481 3.85025ZM4.38146 3.42498C3.22717 3.42498 2.64338 3.59366 2.33481 3.85025L2.48095 4.02599C2.7228 3.82488 3.23322 3.65356 4.38146 3.65356V3.42498ZM9.06756 3.42498H4.38146V3.65356H9.06756V3.42498ZM11.1142 3.85025C10.8056 3.59366 10.2219 3.42498 9.06756 3.42498V3.65356C10.2158 3.65356 10.7262 3.82488 10.9681 4.02599L11.1142 3.85025ZM11.5748 5.40636C11.5748 4.48983 11.3875 4.07751 11.1142 3.85025L10.9681 4.02599C11.1621 4.18732 11.3463 4.51216 11.3463 5.40636H11.5748ZM11.5748 10.5978V5.40636H11.3463V10.5978H11.5748ZM11.1119 12.1535C11.3863 11.9257 11.5748 11.5129 11.5748 10.5978H11.3463C11.3463 11.49 11.1611 11.8156 10.9659 11.9776L11.1119 12.1535ZM9.06756 12.5791C10.2176 12.5791 10.8021 12.4107 11.1119 12.1535L10.9659 11.9776C10.7227 12.1795 10.2113 12.3506 9.06756 12.3506V12.5791ZM6.87511 12.5791H9.06756V12.3506H6.87511V12.5791ZM6.76082 12.4649V13.9221H6.98939V12.4649H6.76082ZM9.06756 13.8078H6.87511V14.0364H9.06756V13.8078ZM11.8966 13.099C11.2294 13.6527 10.2529 13.8078 9.06756 13.8078V14.0364C10.2597 14.0364 11.309 13.8837 12.0425 13.2749L11.8966 13.099ZM12.8035 10.5978C12.8035 11.6316 12.5988 12.5162 11.8966 13.099L12.0425 13.2749C12.8245 12.6259 13.0321 11.6533 13.0321 10.5978H12.8035ZM12.8035 5.40636V10.5978H13.0321V5.40636H12.8035ZM11.8998 2.90552C12.6013 3.48882 12.8035 4.37399 12.8035 5.40636H13.0321C13.0321 4.3528 12.8272 3.37944 12.0459 2.72977L11.8998 2.90552ZM9.06756 2.19632C10.2561 2.19632 11.2332 2.35126 11.8998 2.90552L12.0459 2.72977C11.3128 2.12019 10.2626 1.96775 9.06756 1.96775V2.19632ZM4.38146 2.19632H9.06756V1.96775H4.38146V2.19632ZM1.54924 2.90552C2.2158 2.35126 3.19296 2.19632 4.38146 2.19632V1.96775C3.1864 1.96775 2.13618 2.12019 1.4031 2.72977L1.54924 2.90552ZM0.645536 5.40636C0.645536 4.37399 0.847765 3.48882 1.54924 2.90552L1.4031 2.72977C0.621811 3.37944 0.416964 4.3528 0.416964 5.40636H0.645536ZM0.645536 10.0829V5.40636H0.416964V10.0829H0.645536ZM0.621033 10.1536C0.949787 9.73615 1.43593 9.46445 1.99543 9.43041L1.98155 9.20226C1.35406 9.24044 0.808708 9.54586 0.441467 10.0121L0.621033 10.1536ZM1.8742 5.40636V9.31634H2.10277V5.40636H1.8742Z"
					fill="white"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M14.2767 5.40384C14.2765 5.40396 14.2769 5.40372 14.2767 5.40384L12.9104 6.40032V9.5966L14.2777 10.5938L14.2793 10.595C14.4765 10.7397 14.582 10.7528 14.618 10.7515C14.6411 10.7231 14.7075 10.6181 14.7075 10.3281V5.67492C14.7075 5.38161 14.6396 5.27708 14.6167 5.2497C14.5817 5.24783 14.4757 5.25946 14.2767 5.40384ZM14.6302 5.25171C14.6302 5.25177 14.6293 5.25159 14.6276 5.25097L14.6302 5.25171ZM14.6324 10.7496C14.6324 10.7496 14.6315 10.75 14.6296 10.7503L14.6324 10.7496ZM15.2332 4.11023C15.8005 4.41825 15.9997 5.0603 15.9997 5.67492V10.3281C15.9997 10.9421 15.8007 11.5855 15.2319 11.8927C14.651 12.2065 14.0083 11.9986 13.5156 11.6373C13.5153 11.6371 13.5159 11.6375 13.5156 11.6373L11.8836 10.4471C11.7168 10.3255 11.6182 10.1315 11.6182 9.92507V6.07184C11.6182 5.86543 11.7168 5.67145 11.8836 5.54982L13.5163 4.35905C14.0102 4.0004 14.6538 3.79566 15.2332 4.11023Z"
					fill="var(--icon-color)"
				/>
			</g>
			<defs>
				<clipPath id="clip0_18567_138177">
					<rect width="16" height="16" fill="white" />
				</clipPath>
			</defs>
		</svg>
	)
}

export const textToVideoIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M4.23543 12.1142C4.56608 12.1492 4.81699 12.4281 4.81699 12.7606V13.7065C4.81699 14.118 5.275 14.3629 5.61691 14.1352L5.61724 14.135L8.4581 12.2453C8.56476 12.1744 8.69 12.1365 8.8181 12.1365H11.3337C12.2998 12.1365 13.093 11.8151 13.6443 11.2638C14.1956 10.7125 14.517 9.91931 14.517 8.95321V4.95321C14.517 3.9871 14.1956 3.1939 13.6443 2.64261C13.093 2.09131 12.2998 1.76987 11.3337 1.76987H4.66699C3.70089 1.76987 2.90769 2.09131 2.35639 2.64261C1.8051 3.1939 1.48366 3.9871 1.48366 4.95321V8.95321C1.48366 9.91931 1.8051 10.7125 2.35639 11.2638C2.82467 11.7321 3.46461 12.0326 4.23543 12.1142Z"
				stroke="var(--icon-color)"
				strokeWidth="1.3"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M6.92839 5.88978C6.91425 5.89796 6.78549 5.98582 6.78549 6.36562V8.06467C6.78549 8.44446 6.91425 8.53233 6.92839 8.5405C6.94226 8.54851 7.08192 8.61626 7.40989 8.42687C7.40977 8.42695 7.41002 8.4268 7.40989 8.42687L8.87672 7.57713C9.2048 7.38757 9.21671 7.23214 9.21671 7.21514C9.21671 7.19814 9.2048 7.04272 8.87672 6.85316L7.41027 6.00363C7.41018 6.00358 7.41037 6.00369 7.41027 6.00363C7.08224 5.81419 6.94226 5.88177 6.92839 5.88978ZM6.24205 4.70246C6.8274 4.36409 7.51441 4.47992 8.09637 4.81616L8.09705 4.81655L9.56282 5.66569C9.5627 5.66562 9.56293 5.66575 9.56282 5.66569C10.1448 6.00204 10.5881 6.53951 10.5881 7.21514C10.5881 7.89073 10.1452 8.42797 9.56328 8.76433C9.56312 8.76442 9.56343 8.76424 9.56328 8.76433L8.09637 9.61413C7.51441 9.95037 6.8274 10.0662 6.24205 9.72783C5.65697 9.38962 5.41406 8.73667 5.41406 8.06467V6.36562C5.41406 5.69361 5.65697 5.04067 6.24205 4.70246Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const videoBGRemoverIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<g clipPath="url(#clip0_18567_138196)">
				<path
					d="M3.52711 0.332031C3.97855 0.332031 4.19415 0.887068 3.86107 1.1918L2.49187 2.44447C2.17608 2.73338 1.66748 2.50935 1.66748 2.08134C1.66748 1.94369 1.72512 1.81233 1.82642 1.71914L3.19214 0.462678C3.28346 0.378662 3.40302 0.332031 3.52711 0.332031Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M8.00406 0.332031C8.47714 0.332031 8.70399 0.912867 8.35614 1.2335L2.53125 6.60253C2.20137 6.90659 1.66748 6.67261 1.66748 6.22397C1.66748 6.08036 1.72746 5.94328 1.83294 5.84582L7.65153 0.469957C7.74751 0.381279 7.87338 0.332031 8.00406 0.332031Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M5.71476 7.484C5.78752 7.67482 5.75692 7.89736 5.60428 8.03305L2.49758 10.7946C2.17594 11.0805 1.66748 10.8521 1.66748 10.4218C1.66748 10.2801 1.72777 10.145 1.83329 10.0504L4.95877 7.24828C5.20815 7.02469 5.59543 7.17104 5.71476 7.484Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M3.2499 12.9806C3.44674 12.8042 3.74719 12.8123 3.93415 12.9992C4.209 13.274 3.93416 13.666 3.86748 13.7326L2.49133 14.9836C2.17494 15.2712 1.66748 15.0468 1.66748 14.6192C1.66748 14.4792 1.72703 14.3458 1.83124 14.2524L3.2499 12.9806Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M9.66631 3.19531C7.93298 3.19531 6.46631 4.59531 6.46631 6.39531C6.46631 6.46198 6.46631 6.46198 6.46631 6.52865C6.46631 6.79531 6.53298 7.12865 6.59964 7.39531C6.79964 8.06198 7.26631 8.66198 7.86631 9.06198C8.19964 9.26198 8.53298 9.39531 8.86631 9.52865C9.13298 9.59531 9.39964 9.66198 9.66631 9.66198C11.3996 9.66198 12.8663 8.26198 12.8663 6.46198C12.8663 4.66198 11.3996 3.19531 9.66631 3.19531ZM9.66631 8.52865C8.46631 8.52865 7.53298 7.52865 7.53298 6.39531C7.53298 5.19531 8.53298 4.26198 9.66631 4.26198C10.7996 4.26198 11.7996 5.26198 11.7996 6.39531C11.7996 7.52865 10.8663 8.52865 9.66631 8.52865Z"
					fill="var(--icon-color)"
				/>
				<path
					d="M5.0641 14.8668C4.73076 14.8668 4.53076 14.6001 4.53076 14.3335C4.53076 12.8668 5.13076 10.4668 9.2641 10.4668C9.34326 10.4668 9.42149 10.4677 9.49877 10.4696C9.57606 10.4677 9.65429 10.4668 9.73345 10.4668C13.8668 10.4668 14.4668 12.8668 14.4668 14.3335C14.4668 14.6001 14.2668 14.8668 13.9335 14.8668C13.9207 14.8668 13.9082 14.8665 13.8959 14.8659C13.8857 14.8665 13.8751 14.8668 13.8641 14.8668C13.5308 14.8668 13.3308 14.6001 13.3308 14.3335C13.3308 13.0711 12.8673 11.5439 9.49877 11.5335C6.13027 11.5439 5.66679 13.0711 5.66679 14.3335C5.66679 14.6001 5.46678 14.8668 5.13345 14.8668C5.12241 14.8668 5.11181 14.8665 5.10163 14.8659C5.08931 14.8665 5.0768 14.8668 5.0641 14.8668Z"
					fill="var(--icon-color)"
				/>
			</g>
			<defs>
				<clipPath id="clip0_18567_138196">
					<rect width="16" height="16" fill="white" />
				</clipPath>
			</defs>
		</svg>
	)
}

export const musicGeneratorIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M4.87023 9.72266C3.79634 9.72266 2.92578 10.5932 2.92578 11.6671C2.92578 12.741 3.79634 13.6115 4.87023 13.6115C5.94411 13.6115 6.81467 12.741 6.81467 11.6671C6.81467 10.5932 5.94411 9.72266 4.87023 9.72266ZM1.92578 11.6671C1.92578 10.0409 3.24405 8.72266 4.87023 8.72266C6.4964 8.72266 7.81467 10.0409 7.81467 11.6671C7.81467 13.2933 6.4964 14.6115 4.87023 14.6115C3.24405 14.6115 1.92578 13.2933 1.92578 11.6671Z"
				fill="var(--icon-color)"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M7.31543 2.61072C7.59157 2.61072 7.81543 2.83458 7.81543 3.11072V11.6663C7.81543 11.9424 7.59157 12.1663 7.31543 12.1663C7.03929 12.1663 6.81543 11.9424 6.81543 11.6663V3.11072C6.81543 2.83458 7.03929 2.61072 7.31543 2.61072Z"
				fill="var(--icon-color)"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M8.0815 2.47929C7.9425 2.57936 7.81816 2.77766 7.82034 3.10834L7.82036 3.11163L7.82035 3.83274C7.82035 4.05295 7.90885 4.31008 8.07601 4.54104C8.24296 4.7717 8.46049 4.93766 8.67076 5.00861C8.67099 5.00869 8.67123 5.00877 8.67146 5.00885L11.3703 5.90641C11.3704 5.90644 11.3701 5.90637 11.3703 5.90641C11.6892 6.01189 11.918 5.9556 12.0564 5.85572C12.1947 5.75588 12.3204 5.55677 12.3204 5.21996V4.50496C12.3204 4.28475 12.2318 4.02762 12.0647 3.79667C11.8977 3.56602 11.6802 3.40006 11.47 3.32911C11.4697 3.32903 11.4695 3.32894 11.4692 3.32886L8.76978 2.43108L8.7675 2.43032C8.45007 2.32305 8.22065 2.37912 8.0815 2.47929ZM9.08644 1.48254C8.51815 1.29085 7.9414 1.34798 7.49725 1.66772C7.05307 1.98748 6.81689 2.51568 6.82035 3.11317V3.83274C6.82035 4.30309 7.00074 4.76096 7.26594 5.12736C7.53117 5.49381 7.90917 5.80746 8.35258 5.95664L8.35423 5.95719L11.056 6.85574C11.623 7.04344 12.198 6.98679 12.6417 6.66657C13.0854 6.34627 13.3204 5.81816 13.3204 5.21996V4.50496C13.3204 4.03462 13.14 3.57675 12.8748 3.21034C12.6095 2.8439 12.2315 2.53025 11.7881 2.38107L9.08644 1.48254Z"
				fill="var(--icon-color)"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M15.0013 12.1514V8.8243C14.979 8.19861 14.1707 8.19814 14.1481 8.8243V12.1514C14.1705 12.7771 14.9788 12.7776 15.0013 12.1514ZM12.5893 9.12852C12.442 8.65663 12.0423 8.3529 11.5718 8.35487C11.1007 8.35674 10.7034 8.66392 10.5596 9.13743C10.5564 9.14793 9.89374 12.0471 9.89374 12.0471C9.8361 12.2995 9.99339 12.5509 10.245 12.6087C10.4969 12.6665 10.7475 12.5088 10.8051 12.2564L10.9308 11.7064H12.241L12.3724 12.2602C12.4311 12.514 12.6908 12.6682 12.9354 12.6078C13.1865 12.5479 13.3418 12.2951 13.282 12.0433C13.282 12.0433 12.5925 9.13895 12.5893 9.12852ZM11.1618 10.9145L11.4676 9.33042C11.4909 9.25157 11.5313 9.20829 11.5823 9.20805H11.5828C11.6339 9.20805 11.6733 9.24957 11.697 9.32836L12.015 10.9145H11.1618Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const photoAiIcon = (
	width?: string,
	height?: string,
) => (
	<svg
		width={width ?? "16"}
		height={height ?? "16"}
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			d="M5.59354 6.70661C6.2881 6.70661 6.85114 6.14356 6.85114 5.44901C6.85114 4.75445 6.2881 4.19141 5.59354 4.19141C4.89899 4.19141 4.33594 4.75445 4.33594 5.44901C4.33594 6.14356 4.89899 6.70661 5.59354 6.70661Z"
			fill="var(--icon-color)"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M14.867 14.5416V10.9073C14.8426 10.2239 13.9597 10.2233 13.9351 10.9073V14.5416C13.9595 15.225 14.8424 15.2255 14.867 14.5416ZM12.2318 11.2396C12.0709 10.7242 11.6343 10.3924 11.1204 10.3945C10.6057 10.3966 10.1718 10.7321 10.0147 11.2493C10.0113 11.2608 9.28742 14.4276 9.28742 14.4276C9.22445 14.7033 9.39626 14.9779 9.67115 15.041C9.94629 15.1042 10.22 14.9319 10.2829 14.6562L10.4202 14.0555H11.8513L11.9948 14.6604C12.059 14.9376 12.3427 15.106 12.6098 15.04C12.8842 14.9746 13.0537 14.6985 12.9884 14.4234C12.9884 14.4234 12.2353 11.251 12.2318 11.2396ZM10.6724 13.1901L11.0064 11.4598C11.0318 11.3737 11.076 11.3264 11.1317 11.3262H11.1322C11.1881 11.3262 11.2311 11.3715 11.257 11.4576L11.6043 13.1901H10.6724Z"
			fill="var(--icon-color)"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M2.43522 2.43937C3.29827 1.57631 4.57463 1.26221 6.16441 1.26221H9.83107C11.4208 1.26221 12.6972 1.57631 13.5603 2.43937C14.4233 3.30242 14.7374 4.57878 14.7374 6.16856V7.99841C14.7384 8.01838 14.7384 8.03839 14.7374 8.05838V9.47269C14.6679 9.46119 14.5966 9.45525 14.5241 9.45523C14.1013 9.45511 13.72 9.65613 13.4792 9.98924C13.4799 9.93865 13.4803 9.88732 13.4803 9.83522V8.31174L12.7153 7.63868L12.7152 7.63867C12.4841 7.43531 12.0829 7.43531 11.8518 7.63867L11.8515 7.63886L9.35293 9.83524L9.35271 9.83543C8.64677 10.4564 7.53445 10.4563 6.82859 9.83524L6.82324 9.83053L6.82327 9.8305L6.6282 9.65491C6.42199 9.47546 6.05715 9.45093 5.81579 9.6168L2.85463 11.6533C2.8234 11.6747 2.79091 11.693 2.75755 11.7082C2.89441 12.1234 3.08603 12.4374 3.32415 12.6755C3.8361 13.1874 4.69862 13.4844 6.16441 13.4844H8.53487L8.33899 14.3406C8.30819 14.4754 8.29862 14.6103 8.30811 14.7416H6.16441C4.57463 14.7416 3.29827 14.4275 2.43522 13.5644C1.57216 12.7014 1.25806 11.425 1.25806 9.83522V6.16856C1.25806 4.57878 1.57216 3.30242 2.43522 2.43937ZM13.4803 6.16856V6.64C12.7726 6.07436 11.7061 6.09257 11.0215 6.69466L11.0215 6.69471L11.0213 6.69485L8.52272 8.89123L8.5225 8.89142C8.29209 9.09414 7.89264 9.09478 7.66118 8.89331L7.46611 8.71772L7.46614 8.71769L7.46084 8.71303C6.8137 8.14352 5.81067 8.09494 5.10379 8.58071L5.1036 8.58084L2.52866 10.3517C2.51974 10.1884 2.5152 10.0164 2.5152 9.83522V6.16856C2.5152 4.70277 2.81221 3.84025 3.32415 3.3283C3.8361 2.81636 4.69862 2.51935 6.16441 2.51935H9.83107C11.2969 2.51935 12.1594 2.81636 12.6713 3.3283C13.1833 3.84025 13.4803 4.70277 13.4803 6.16856Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

export const textToImageIcon = (
	width?: string,
	height?: string,
) => (
	<svg
		width={width ?? "16"}
		height={height ?? "16"}
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.23269 12.1357C4.55243 12.1696 4.79508 12.4393 4.79508 12.7608V13.7068C4.79508 14.1355 5.27228 14.3904 5.6283 14.1533L5.62862 14.1531L8.46948 12.2634C8.57262 12.1948 8.69373 12.1582 8.81761 12.1582H11.3332C12.3041 12.1582 13.1031 11.8351 13.6589 11.2792C14.2148 10.7233 14.5379 9.92442 14.5379 8.95345V4.95345C14.5379 3.98248 14.2148 3.18356 13.6589 2.6277C13.1031 2.07184 12.3041 1.74869 11.3332 1.74869H4.6665C3.69553 1.74869 2.89661 2.07184 2.34075 2.6277C1.78489 3.18356 1.46174 3.98248 1.46174 4.95345V8.95345C1.46174 9.92442 1.78489 10.7233 2.34075 11.2792C2.81286 11.7513 3.45752 12.0537 4.23269 12.1357Z"
			stroke="var(--icon-color)"
			strokeWidth="1.25714"
			strokeMiterlimit="10"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M14.6665 6.33301L11.5556 8.63654C10.5326 9.39405 9.17471 9.52413 8.02649 8.97463V8.97463C6.92522 8.44759 5.62703 8.5443 4.61601 9.2287L1.99984 10.9997"
			stroke="var(--icon-color)"
			strokeWidth="1.25714"
			strokeMiterlimit="10"
			strokeLinejoin="round"
		/>
		<path
			d="M9.5127 6.19336C10.2025 6.19336 10.7617 5.63415 10.7617 4.94434C10.7617 4.25452 10.2025 3.69531 9.5127 3.69531C8.82288 3.69531 8.26367 4.25452 8.26367 4.94434C8.26367 5.63415 8.82288 6.19336 9.5127 6.19336Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

export const generativeFillIcon = (
	width?: string,
	height?: string,
) => (
	<svg
		width={width ?? "16"}
		height={height ?? "16"}
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M9.75036 6.44783C9.74966 6.44816 9.74895 6.4485 9.74826 6.44883C9.73627 6.45461 9.72464 6.46114 9.71342 6.46839C9.67772 6.49148 9.64697 6.52144 9.62297 6.55653C9.59897 6.59162 9.5822 6.63114 9.57363 6.67278L9.16546 8.51161C9.07183 8.93457 8.87226 9.30086 8.59467 9.58502C8.307 9.87588 7.94184 10.078 7.54262 10.1673L5.73992 10.5837C5.68011 10.5978 5.62552 10.6286 5.5825 10.6725C5.53947 10.7164 5.50977 10.7716 5.49683 10.8317C5.4778 10.9158 5.49263 11.0041 5.53811 11.0774C5.58359 11.1507 5.65608 11.2032 5.73992 11.2235L7.54262 11.6398C7.94157 11.7297 8.3064 11.9321 8.59385 12.223C8.88111 12.5201 9.07892 12.8921 9.16463 13.2964L9.57281 15.1352C9.58566 15.1953 9.61533 15.2506 9.65837 15.2945C9.70141 15.3384 9.75604 15.3692 9.8159 15.3832C9.85739 15.3925 9.90032 15.3934 9.94216 15.3859C9.98401 15.3784 10.0239 15.3627 10.0596 15.3396C10.0953 15.3165 10.1261 15.2865 10.1501 15.2514C10.1741 15.2163 10.1909 15.1768 10.1994 15.1352L10.6076 13.2964C10.6936 12.8922 10.8913 12.5202 11.1784 12.223C11.4661 11.9321 11.8312 11.73 12.2304 11.6407L14.0323 11.2243C14.0921 11.2101 14.1467 11.1793 14.1897 11.1354C14.2328 11.0915 14.2625 11.0363 14.2754 10.9763C14.2942 10.8922 14.2793 10.8042 14.2338 10.7311C14.1883 10.658 14.116 10.6056 14.0323 10.5853L12.2304 10.1689C11.8165 10.0737 11.4568 9.87082 11.1784 9.58666C10.9 9.3025 10.7012 8.93621 10.6076 8.51326L10.1994 6.67442L10.2003 6.67278C10.1874 6.61265 10.1577 6.55741 10.1147 6.5135C10.0717 6.46959 10.017 6.43881 9.95716 6.42476C9.91567 6.41549 9.87275 6.41458 9.8309 6.42207C9.80297 6.42707 9.77589 6.43575 9.75036 6.44783ZM5.60543 6.6611C5.67496 6.50586 5.76868 6.36766 5.88282 6.25147C6.00582 6.12626 6.15327 6.02477 6.31969 5.95245C6.40107 5.91709 6.48699 5.8887 6.5768 5.86793L7.84485 5.57474C7.90466 5.5606 7.95925 5.52979 8.00227 5.48589C8.0453 5.44199 8.075 5.3868 8.08794 5.32671C8.10673 5.2427 8.09178 5.15466 8.04632 5.08155C8.00086 5.00844 7.92851 4.9561 7.84485 4.93579L6.5768 4.64342C6.48711 4.62265 6.40124 4.59424 6.31986 4.55883C6.15981 4.4892 6.01711 4.39253 5.89679 4.27384L5.88365 4.25906C5.76768 4.13917 5.67376 4.00081 5.60532 3.85039C5.60352 3.84643 5.60174 3.84247 5.59997 3.83849C5.59965 3.83776 5.59932 3.83702 5.599 3.83629C5.55866 3.74508 5.52764 3.64955 5.50668 3.55112L5.22006 2.25679C5.20721 2.19666 5.17753 2.14142 5.13449 2.09751C5.09145 2.0536 5.03682 2.02282 4.97696 2.00877C4.93547 1.9995 4.89255 1.99859 4.8507 2.00608C4.80885 2.01357 4.76891 2.02932 4.73321 2.05241C4.69752 2.07549 4.66677 2.10545 4.64277 2.14054C4.61877 2.17564 4.60199 2.21515 4.59342 2.25679L4.30598 3.55112C4.2848 3.64713 4.25533 3.73884 4.21832 3.82545C4.21659 3.82951 4.21484 3.83355 4.21307 3.83759C4.21117 3.84193 4.20925 3.84626 4.20731 3.85057C4.1406 3.99903 4.05144 4.13192 3.9438 4.2451L3.92901 4.25906C3.80613 4.3847 3.65885 4.4863 3.49287 4.55867C3.41147 4.59415 3.32558 4.62261 3.23586 4.64342L1.96781 4.93579C1.908 4.94993 1.85341 4.98074 1.81039 5.02464C1.76736 5.06853 1.73766 5.12372 1.72472 5.18381C1.70593 5.26783 1.72087 5.35587 1.76634 5.42898C1.8118 5.50209 1.88415 5.55443 1.96781 5.57474L3.23668 5.86793C3.32534 5.88778 3.41144 5.91608 3.49388 5.95224C3.65616 6.0234 3.80427 6.12497 3.92983 6.25229C4.0455 6.37215 4.13916 6.5104 4.20742 6.66065C4.20896 6.66404 4.21049 6.66743 4.212 6.67083C4.21245 6.67183 4.21289 6.67282 4.21333 6.67382C4.25385 6.76541 4.28499 6.86136 4.30598 6.96023L4.59342 8.25456C4.60627 8.31469 4.63595 8.36993 4.67899 8.41384C4.72203 8.45775 4.77666 8.48853 4.83652 8.50258C4.87801 8.51185 4.92093 8.51276 4.96278 8.50527C5.00463 8.49778 5.04457 8.48203 5.08027 8.45894C5.11596 8.43586 5.14671 8.40589 5.17071 8.3708C5.19471 8.33571 5.21149 8.2962 5.22006 8.25456L5.50668 6.96023C5.52807 6.86454 5.55746 6.77311 5.59423 6.68673C5.59621 6.68207 5.59822 6.67743 5.60025 6.6728C5.60196 6.66889 5.60369 6.66499 5.60543 6.6611ZM4.81743 5.25539C4.83831 5.27527 4.8589 5.2955 4.87919 5.31607L4.88426 5.32122L4.88929 5.32642C4.89483 5.33217 4.90035 5.33794 4.90585 5.34373C4.91439 5.3348 4.92299 5.32591 4.93167 5.31708C4.95215 5.29623 4.97294 5.27575 4.99403 5.25563C4.98275 5.24488 4.97155 5.23403 4.96044 5.22307L4.92931 5.19236L4.90691 5.16716L4.88529 5.18989L4.86048 5.21333C4.84627 5.22753 4.83192 5.24155 4.81743 5.25539ZM11.4568 4.85284C11.4697 4.91297 11.4993 4.96821 11.5424 5.01212C11.5854 5.05603 11.64 5.08681 11.6999 5.10086C11.7414 5.11013 11.7843 5.11104 11.8262 5.10355C11.868 5.09606 11.908 5.08031 11.9436 5.05723C11.9793 5.03414 12.0101 5.00418 12.0341 4.96909C12.0581 4.934 12.0749 4.89448 12.0834 4.85284L12.2723 4.00282C12.2786 3.97489 12.2861 3.94752 12.2947 3.92081C12.3116 3.86832 12.3328 3.81834 12.3582 3.77149C12.3604 3.7673 12.3627 3.76314 12.3651 3.75901C12.3947 3.70633 12.4296 3.65778 12.4691 3.61427C12.4759 3.60676 12.4829 3.59939 12.49 3.59218C12.5025 3.57951 12.5154 3.56725 12.5287 3.55544C12.5607 3.52718 12.595 3.50142 12.6316 3.47839C12.6337 3.47709 12.6357 3.47579 12.6378 3.4745C12.6881 3.44343 12.7425 3.41749 12.8003 3.39722C12.8301 3.38675 12.8608 3.3778 12.8924 3.37044L13.726 3.17826C13.7858 3.16412 13.8404 3.13331 13.8834 3.08942C13.9264 3.04552 13.9561 2.99033 13.9691 2.93024C13.9879 2.84622 13.9729 2.75818 13.9275 2.68507C13.882 2.61196 13.8097 2.55962 13.726 2.53931L12.8924 2.34713C12.8609 2.34001 12.8302 2.33125 12.8004 2.32087C12.7406 2.30001 12.6843 2.27265 12.6316 2.23908C12.6304 2.23834 12.6292 2.23759 12.6281 2.23684C12.5932 2.21434 12.5598 2.18909 12.5281 2.16118C12.5174 2.15174 12.5069 2.142 12.4965 2.13196C12.4874 2.12263 12.4785 2.1131 12.4699 2.10339C12.4301 2.05884 12.3953 2.0104 12.3659 1.95893C12.3637 1.95509 12.3615 1.95124 12.3594 1.94737C12.3328 1.89925 12.311 1.84859 12.2941 1.79606C12.2855 1.76941 12.2783 1.74228 12.2723 1.71475L12.0834 0.864735C12.0706 0.804605 12.0401 0.749364 11.9971 0.705452C11.954 0.661539 11.8994 0.630763 11.8395 0.61671C11.798 0.607443 11.7551 0.60653 11.7133 0.614022C11.6714 0.621514 11.6315 0.637262 11.5958 0.660348C11.5601 0.683433 11.5293 0.713395 11.5053 0.748486C11.4813 0.783577 11.4646 0.823094 11.456 0.864735L11.2671 1.71475C11.2612 1.7426 11.2538 1.77006 11.2452 1.79702C11.2281 1.8503 11.2058 1.90165 11.1786 1.95035C11.1771 1.9532 11.1755 1.95603 11.1738 1.95886C11.1444 2.01021 11.1096 2.05853 11.0698 2.10294C11.0653 2.10801 11.0607 2.11303 11.056 2.118C11.0416 2.13284 11.0267 2.1471 11.0113 2.16076C10.9791 2.18924 10.9449 2.21509 10.9089 2.2381C10.9063 2.23975 10.9037 2.24139 10.9011 2.24301C10.8505 2.27457 10.7965 2.30058 10.7401 2.32051C10.71 2.33114 10.6792 2.34004 10.6479 2.34713L9.81426 2.53931C9.75444 2.55346 9.69986 2.58426 9.65683 2.62816C9.61381 2.67206 9.58411 2.72725 9.57116 2.78734C9.55238 2.87136 9.56732 2.95939 9.61279 3.0325C9.65825 3.10561 9.7306 3.15795 9.81426 3.17826L10.6479 3.37044C10.6791 3.37746 10.7098 3.38626 10.7398 3.39678C10.797 3.41684 10.8518 3.44314 10.9031 3.47514C10.905 3.47631 10.9069 3.47748 10.9087 3.47867C10.945 3.50168 10.9795 3.52755 11.0119 3.55609C11.025 3.56769 11.0378 3.57972 11.0503 3.59218C11.0572 3.59943 11.0641 3.6068 11.0708 3.61428C11.1099 3.65804 11.1443 3.7056 11.1734 3.75612C11.1739 3.75707 11.1745 3.75802 11.175 3.75898C11.2043 3.81024 11.2282 3.86453 11.2462 3.92098C11.2548 3.9478 11.262 3.97512 11.2679 4.00282L11.4568 4.85284ZM9.88577 10.114C9.98828 10.2574 10.1019 10.3931 10.226 10.5198C10.365 10.6616 10.5155 10.7904 10.6759 10.9052C10.5176 11.0187 10.3685 11.1457 10.2304 11.2854L10.2248 11.291L10.2192 11.2968C10.0981 11.4222 9.98687 11.556 9.88622 11.6968C9.78535 11.5557 9.67386 11.4218 9.55242 11.2962L9.54735 11.2909L9.54223 11.2857C9.40368 11.1455 9.25406 11.018 9.0952 10.9042C9.25422 10.7904 9.40399 10.6628 9.54268 10.5226L9.54844 10.5167C9.67124 10.391 9.78388 10.2564 9.88577 10.114Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

export const lookAiIcon = (
	width?: string,
	height?: string,
) => (
	<svg
		width={width ?? "16"}
		height={height ?? "16"}
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			d="M11.267 10.9331C11.0003 9.93311 9.53364 9.93311 9.26697 10.9331L8.66697 13.7998C8.46697 14.3998 9.46697 14.5998 9.60031 13.9998L9.73364 13.4664H10.9336C11.067 13.7998 11.067 14.5331 11.667 14.3998C11.9336 14.3331 12.067 14.1331 12.0003 13.8664L11.267 10.9331ZM9.86697 12.7331L10.1336 11.1998C10.1336 11.1331 10.2003 11.0664 10.267 11.0664C10.3336 11.0664 10.3336 11.1331 10.4003 11.1998L10.667 12.7331H9.86697Z"
			fill="var(--icon-color)"
		/>
		<path
			d="M12.7998 10.5996V13.9329C12.7998 14.5329 13.6665 14.5329 13.6665 13.9329V10.5996C13.6665 9.93294 12.7998 9.93294 12.7998 10.5996Z"
			fill="var(--icon-color)"
		/>
		<path
			d="M15.0669 6.19987L13.4669 3.19987C13.0002 2.19987 11.9336 1.86654 10.9336 1.5332C10.5336 3.86654 5.46689 3.86654 5.06689 1.5332C4.06689 1.86654 3.00022 2.19987 2.53355 3.19987L0.933554 6.19987C0.80022 6.39987 0.933554 6.66654 1.13355 6.7332L3.46689 7.66654C3.53355 7.7332 3.60022 7.79987 3.60022 7.86654L3.20022 14.0665C3.20022 14.2665 3.40022 14.4665 3.60022 14.4665H8.20022C7.93355 14.1332 7.86689 13.6665 7.93355 13.3332L8.53355 10.6665C8.86689 9.19987 10.9336 8.7332 11.8669 9.9332C12.0002 9.7332 12.1336 9.46654 12.4002 9.3332L12.3336 7.79987C12.3336 7.7332 12.4002 7.59987 12.4669 7.59987L14.8002 6.66654C15.0669 6.66654 15.1336 6.3332 15.0669 6.19987ZM12.0669 6.59987C11.4002 6.86654 11.1336 7.59987 11.2669 8.19987C9.66689 7.7332 7.93355 8.7332 7.53355 10.3999V10.4665L6.93355 13.0665C6.93355 13.1332 6.86689 13.2665 6.86689 13.3332H4.46689L4.80022 7.9332C4.86689 7.3332 4.46689 6.79987 3.93355 6.59987L2.33355 5.9332L3.53355 3.66654C3.73355 3.26654 4.06689 2.99987 4.46689 2.86654C5.86689 4.86654 10.0669 4.79987 11.4669 2.86654C11.8669 2.99987 12.2002 3.26654 12.4002 3.66654L13.6002 5.9332L12.0669 6.59987Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

export const aiPhotoshootIcon = (
	width?: string,
	height?: string,
) => {
	return (
		<svg
			width={width ?? "16"}
			height={height ?? "16"}
			viewBox="0 0 20 20"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M8.75162 1.16887C9.13124 0.950115 9.56168 0.834961 9.99984 0.834961C10.438 0.834961 10.8685 0.950124 11.2481 1.16889L11.2498 1.1699L17.08 4.50138L17.0832 4.50322C17.4628 4.72243 17.7782 5.03762 17.9976 5.41718C18.217 5.79674 18.3327 6.22732 18.3332 6.66573V6.66658V13.3333V13.3341C18.3327 13.7725 18.217 14.2031 17.9976 14.5827C17.7782 14.9622 17.4628 15.2774 17.0832 15.4966L17.08 15.4985L11.2498 18.8299L11.2483 18.8308C10.8876 19.0387 10.481 19.1531 10.0655 19.164C10.0439 19.1657 10.0219 19.1666 9.99984 19.1666C9.97773 19.1666 9.95582 19.1657 9.93415 19.164C9.51865 19.1531 9.11206 19.0387 8.75138 18.8308L8.74984 18.8299L2.91972 15.4985L2.9165 15.4966C2.53683 15.2774 2.22148 14.9622 2.00208 14.5827C1.78268 14.2031 1.66695 13.7725 1.6665 13.3341V13.3333V6.66658V6.66573C1.66695 6.22732 1.78268 5.79674 2.00208 5.41718C2.22148 5.03762 2.53683 4.72243 2.9165 4.50323L2.91972 4.50138L2.91972 4.50138L8.75162 1.16887ZM10.8332 17.1485L16.2498 14.0532L16.2512 14.0525C16.3771 13.9794 16.4818 13.8747 16.5546 13.7486C16.6278 13.6221 16.6664 13.4785 16.6665 13.3324V7.12966L10.8332 10.4821V17.1485ZM9.1665 10.4821V17.1485L3.74984 14.0532L3.74852 14.0525C3.62255 13.9794 3.51791 13.8747 3.44503 13.7486C3.37196 13.6222 3.33338 13.4788 3.33317 13.3328V7.12961L9.1665 10.4821ZM9.99984 2.50163C9.85356 2.50163 9.70985 2.54013 9.58317 2.61327L9.57996 2.61513L9.57995 2.61512L4.18594 5.6974L9.99988 9.03874L15.8138 5.69743L10.4197 2.61512L10.4165 2.61328L10.4165 2.61327C10.2898 2.54013 10.1461 2.50163 9.99984 2.50163Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

export const ArrowIcon = () => (
	<svg
		width="16"
		height="17"
		viewBox="0 0 16 17"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M13.67 8.02876C13.795 8.15378 13.8652 8.32335 13.8652 8.50016C13.8652 8.67697 13.795 8.84654 13.67 8.97157L10.4779 12.1636C10.2176 12.424 9.79545 12.424 9.53511 12.1636C9.27476 11.9033 9.27476 11.4812 9.53511 11.2208L11.5891 9.16683H2.80162C2.43343 9.16683 2.13495 8.86835 2.13495 8.50016C2.13495 8.13197 2.43343 7.8335 2.80162 7.8335L11.5891 7.8335L9.53511 5.77951C9.27476 5.51916 9.27476 5.09705 9.53511 4.8367C9.79545 4.57635 10.2176 4.57635 10.4779 4.8367L13.67 8.02876Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

const SystemIcon = () => (
	<svg
		width="16"
		height="17"
		viewBox="0 0 16 17"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			d="M13.333 11.1663V5.16634C13.333 4.81272 13.1925 4.47358 12.9425 4.22353C12.6924 3.97348 12.3533 3.83301 11.9997 3.83301H3.99969C3.64607 3.83301 3.30693 3.97348 3.05688 4.22353C2.80683 4.47358 2.66636 4.81272 2.66636 5.16634V11.1663M13.333 11.1663H2.66636M13.333 11.1663L14.1864 12.8663C14.2378 12.9683 14.2621 13.0818 14.257 13.1959C14.252 13.31 14.2177 13.4209 14.1574 13.5179C14.0972 13.615 14.0131 13.6949 13.9131 13.7501C13.8131 13.8052 13.7006 13.8338 13.5864 13.833H2.41302C2.29881 13.8338 2.18632 13.8052 2.08632 13.7501C1.98632 13.6949 1.90217 13.615 1.84194 13.5179C1.78171 13.4209 1.74742 13.31 1.74235 13.1959C1.73729 13.0818 1.76162 12.9683 1.81302 12.8663L2.66636 11.1663"
			stroke="var(--icon-color)"
			strokeWidth="1.09091"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

const DarkIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M13.4755 10.5806C12.6764 10.9095 11.7985 11.0908 10.8795 11.0908C7.25459 11.0908 4.24243 8.25443 4.24243 4.66657C4.24243 4.18148 4.29817 3.70832 4.40381 3.25312C3.18806 4.33627 2.42425 5.91267 2.42425 7.66657C2.42425 10.9301 5.06984 13.5757 8.33334 13.5757C10.5361 13.5757 12.4592 12.3699 13.4755 10.5806ZM13.8178 9.1908C14.3697 8.85866 15.1877 9.23439 14.9872 9.84653C14.0709 12.6451 11.4382 14.6666 8.33334 14.6666C4.46735 14.6666 1.33334 11.5326 1.33334 7.66657C1.33334 5.04927 2.76977 2.76747 4.89729 1.5665C5.48131 1.23683 5.99847 2.04539 5.73786 2.66333C5.47701 3.28183 5.33334 3.95807 5.33334 4.66657C5.33334 7.61209 7.81645 9.99991 10.8795 9.99991C11.9587 9.99991 12.9658 9.70353 13.8178 9.1908Z"
			fill="var(--icon-color)"
		/>
	</svg>
)

const LightIcon = () => (
	<svg
		width="16"
		height="17"
		viewBox="0 0 16 17"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<g clipPath="url(#clip0_24733_53351)">
			<circle
				cx="8.00016"
				cy="8.49967"
				r="2.66667"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinejoin="round"
			/>
			<path
				d="M13.001 8.5H14.3343"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M1.66748 8.5H3.00081"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M8 13.499L8 14.8324"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M8 2.16602L8 3.49935"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M11.5332 12.0352L12.476 12.978"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M3.52002 4.02148L4.46283 4.96429"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M4.46289 12.0352L3.52008 12.978"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
			<path
				d="M12.4761 4.02148L11.5333 4.96429"
				stroke="var(--icon-color)"
				strokeWidth="1.33333"
				strokeLinecap="round"
			/>
		</g>
		<defs>
			<clipPath id="clip0_24733_53351">
				<rect
					width="16"
					height="16"
					fill="white"
					transform="translate(0 0.5)"
				/>
			</clipPath>
		</defs>
	</svg>
)

interface LogoProps {
	dark?: boolean
	hideHiring?: boolean
	showOnMobile?: boolean
}

const Logo = (props: LogoProps) => (
	<BigLogo showOnMobile={props.showOnMobile ?? false}>
		<LogoLink href="/">
			{props.dark ? (
				<img
					src={assetUrl("/logos/logo-dark.svg")}
					alt="Vidsup logo dark theme"
					className="h-[19px] w-[40px]"
				/>
			) : (
				<CustomThemedResource
					source="/logos/logo"
					format="svg"
					alt="Vidsup main logo"
					className="mt-2"
				/>
			)}
		</LogoLink>
	</BigLogo>
)

const LogoSmall = () => {
	return (
		<a href="/">
			<CustomThemedResource
				className="h-8"
				source="/logos/logo"
				alt="Vidsup small logo"
				format="svg"
			/>
		</a>
	)
}

export function LogoSmallScaled() {
	return (
		<a href="/">
			<CustomThemedResource
				className="h-10"
				source="/logos/logo"
				alt="Vidsup small logo icon"
				format="svg"
			/>
		</a>
	)
}

const GeneralLine = styled.div`
	position: absolute;
	left: 5px;

	height: 2px;
	width: 20px;

	border-radius: 4px;

	background: var(--color-blue-900);
	transform: rotate(0deg);

	transition: opacity 300ms, transform 300ms;
	transition-timing-function: ease-in-out;
`

const FirstLine = styled(GeneralLine)`
	top: 7px;
`

const SecondLine = styled(GeneralLine)`
	top: 15px;
`

const HiddenLine = styled(GeneralLine)`
	top: 15px;
`

const ThirdLine = styled(GeneralLine)`
	top: 23px;
`

const HamburgerContainer = styled.div<{ show: boolean }>`
	width: 32px;
	height: 32px;
	position: relative;

	${FirstLine} {
		${(props) =>
			props.show &&
			css`
				opacity: 0;
			`}
	}

	${ThirdLine} {
		${(props) =>
			props.show &&
			css`
				opacity: 0;
			`}
	}

	${SecondLine} {
		${(props) =>
			props.show &&
			css`
				transform: rotate(-45deg);
			`}
	}

	${HiddenLine} {
		${(props) =>
			props.show &&
			css`
				transform: rotate(45deg);
			`}
	}
`

interface HamburgerAnimatedProps {
	onClick: () => void
	show: boolean
}

export const HamburgerAnimated = (
	props: HamburgerAnimatedProps,
) => {
	const { onClick, show } = props

	return (
		<HamburgerContainer onClick={onClick} show={show}>
			<FirstLine />
			<SecondLine />
			<HiddenLine />
			<ThirdLine />
		</HamburgerContainer>
	)
}

export { Logo, LogoSmall, ThemeToggle }
